const courier = { id: 'visual-courier', name: 'Visual Courier' };
const sender = { id: 'visual-sender', name: 'Visual Sender', isActivated: true };
const status = { id: 'visual-status', name: 'Visual Status', priority: 1, associatedState: 1, isRemoved: false };

const order = {
  id: 'order-1',
  outsideId: 'VISUAL-ORDER-01',
  lgStandingData: { outProjectId: 'VISUAL-PROJECT', outOrderId: 'VISUAL-ORDER-01' },
  statusId: status.id,
  statusChangeAt: '2026-09-29T09:00:00Z',
  state: 'NEW',
  recipient: {
    name: 'Visual Recipient',
    postcode: '101000',
    country: 'Russia',
    region: 'Moscow',
    city: 'Moscow',
    address: 'Visual Street',
    house: '1',
    flat: '1',
    email: 'recipient@example.invalid',
    phone: '+70000000000',
  },
  logistic: null,
  cart: { totalPrice: 1250, totalQuantity: 1, totalWeight: 0.5, goods: [] },
  comment: 'Synthetic order',
  shippingCost: 150,
  reimbursementAmount: 1250,
  storehouseId: null,
  additions: {},
  createdAt: '2026-09-28T09:00:00Z',
  updatedAt: '2026-09-29T09:00:00Z',
  returnedAt: null,
  boughtOutAt: null,
  outTotalPrice: 1250,
};

const claim = {
  id: 'claim-1',
  sequenceId: 1,
  externalId: 'VISUAL-CLAIM-01',
  sender,
  courier,
  user: { id: 'user-visual-only', username: 'visual@example.invalid', fullName: 'Synthetic Visual Developer', role: 'ROLE_DEVELOPER' },
  project: 'project-demo',
  status: 0,
  createdAt: '2026-09-29T09:00:00Z',
  orderTotalPrice: 1250,
  orderCount: 1,
  totalReimbursementAmount: 1250,
  comment: 'Synthetic claim',
};

const monthlyTotals: Record<string, { totalSum: number; totalCount: number }> = {
  '2026-04': { totalSum: 0, totalCount: 0 },
  '2026-05': { totalSum: 840, totalCount: 2 },
  '2026-06': { totalSum: 1650, totalCount: 3 },
  '2026-07': { totalSum: 1275, totalCount: 2 },
  '2026-08': { totalSum: 2450, totalCount: 5 },
  '2026-09': { totalSum: 3120, totalCount: 6 },
};

function isEmptyList(body: unknown): boolean {
  return Array.isArray(body) && body.length === 0;
}

function isEmptyPage(body: unknown): boolean {
  return body !== null && typeof body === 'object' && 'output' in body && 'totalCount' in body
    && isEmptyList(body.output) && body.totalCount === 0;
}

function isEmptyCount(body: unknown): boolean {
  return body !== null && typeof body === 'object' && !Array.isArray(body)
    && Object.values(body).length > 0 && Object.values(body).every(value => value === 0);
}

function debtsForWindow(params: URLSearchParams) {
  const from = params.get('unloadedFrom');
  if (!from || !/^\d{4}-\d{2}-\d{2}$/.test(from)) {
    return undefined;
  }

  const firstMonth = new Date(`${from}T00:00:00Z`);
  if (Number.isNaN(firstMonth.getTime())) {
    return undefined;
  }

  const debtsByMonth = Array.from({ length: 6 }, (_, index) => {
    const month = new Date(Date.UTC(firstMonth.getUTCFullYear(), firstMonth.getUTCMonth() + index, 1));
    const year = month.getUTCFullYear();
    const monthNumber = month.getUTCMonth() + 1;
    const totals = monthlyTotals[`${year}-${String(monthNumber).padStart(2, '0')}`] ?? { totalSum: 0, totalCount: 0 };
    return { year, month: monthNumber, ...totals };
  });

  return [{ courier, sender, debtsByMonth }];
}

function previewDate(value: string | null): Date | undefined {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return undefined;
  }

  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value ? date : undefined;
}

function datesInWindow(from: string | null, to: string | null): Date[] | undefined {
  const first = previewDate(from);
  const last = previewDate(to);
  if (!first || !last || first > last) {
    return undefined;
  }

  const days = Math.round((last.getTime() - first.getTime()) / 86400000);
  const intervals = Math.min(days, 6);
  return Array.from({ length: intervals + 1 }, (_, index) =>
    new Date(first.getTime() + Math.round(index * days / (intervals || 1)) * 86400000));
}

function courierDebtsForWindow(params: URLSearchParams) {
  const dates = datesInWindow(params.get('unloadedFrom'), params.get('unloadedTo'));
  if (!dates || !previewDate(params.get('createdAt'))) {
    return undefined;
  }

  return [{
    courierId: courier.id,
    debts: dates.map((date, index) => ({
      unloadedAt: date.toISOString(),
      debt: [840, 1250, 950, 1780, 1430, 2110, 1650][index],
      count: index + 1,
    })),
  }];
}

function claimCountsForWindow(params: URLSearchParams) {
  const filter = params.get('filter');
  const range = filter?.match(/^createdAt:between\{(\d{4}-\d{2}-\d{2}) [^,}]+,(\d{4}-\d{2}-\d{2}) [^}]+\}(?: ordering createdAt:asc)?$/);
  if (!range) {
    return undefined;
  }

  const dates = datesInWindow(range[1], range[2]);
  return dates?.flatMap((date, index) => [1, 2, 3].map(type => ({
    type,
    count: (index + type) % 4 + 1,
    createdAt: date.toISOString(),
  })));
}

export function localProjectPreviewBody(path: string, body: unknown, params: URLSearchParams): unknown | undefined {
  if (path === '/api/auth/currency-rates' && body !== null && typeof body === 'object' && 'rates' in body && isEmptyList(body.rates)) {
    return { ...body, rates: [{ key: 'USDRUB', value: '90' }] };
  }

  if (isEmptyPage(body)) {
    if (path === '/api/project/orders')
      return { totalCount: 1, output: [order] };
    if (path === '/api/project/claims')
      return { totalCount: 1, output: [claim] };
  }

  if (isEmptyList(body)) {
    switch (path) {
      case '/api/project/analytic/debt': return debtsForWindow(params);
      case '/api/project/debts': return courierDebtsForWindow(params);
      case '/api/project/analytic/claim': return claimCountsForWindow(params);
      case '/api/project/statuses': return [status];
      case '/api/project/couriers': return [courier];
      case '/api/project/senders': return [sender];
      case '/api/project/statuses/order-count': return [{ statusId: status.id, orderCount: 1 }];
      case '/api/project/transferred-unpaid-count':
        return [...params.values()].includes(claim.id) ? [{ claimId: claim.id, unpaidCount: 1 }] : undefined;
    }
  }

  if (path === '/api/project/claim-count' && isEmptyCount(body))
    return { inProcess: 1, accepted: 0, declined: 0, paid: 0 };

  return undefined;
}

export function localProjectPreviewErrorBody(path: string, params: URLSearchParams): unknown | undefined {
  switch (path) {
    case '/api/project/orders':
    case '/api/project/claims':
      return localProjectPreviewBody(path, { totalCount: 0, output: [] }, params);
    case '/api/project/claim-count':
      return localProjectPreviewBody(path, { inProcess: 0, accepted: 0, declined: 0, paid: 0 }, params);
    case '/api/project/debts':
    case '/api/project/analytic/claim':
    case '/api/project/analytic/debt':
    case '/api/project/couriers':
    case '/api/project/senders':
    case '/api/project/statuses':
    case '/api/project/statuses/order-count':
    case '/api/project/transferred-unpaid-count':
      return localProjectPreviewBody(path, [], params);
  }
  return undefined;
}
